import {
  Badge,
  IcnArrowRight,
  PrimitiveButton,
} from '@viquard/doc-ui';
import type { TFunction, i18n as I18nInstance } from 'i18next';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { iconForScope, type SearchHitScope } from './search-scope.js';

export { SEARCH_HIT_SCOPES } from './search-scope.js';
export type { SearchHitScope } from './search-scope.js';

export type SearchResultCardVariant = 'page' | 'dialog' | 'suggestion';

export interface SearchResultCardProps {
  scope: SearchHitScope;
  title: string;
  summary: string | null;
  status: string | null;
  metadata?: Record<string, unknown>;
  url: string;
  query: string;
  variant?: SearchResultCardVariant;
  showScope?: boolean;
  onOpen?: () => void;
}

export function SearchResultCard(props: SearchResultCardProps): ReactNode {
  const {
    scope,
    title,
    summary,
    status,
    metadata,
    url,
    query,
    variant = 'page',
    showScope = false,
    onOpen,
  } = props;
  const { t, i18n } = useTranslation('common');
  const scopeLabel = t(`search.scopes.${scope}`);
  const titleLabel = localizeSearchTitle(scope, title, t, i18n);
  const summaryLabel = localizeSearchSummary(scope, summary, metadata, t, i18n);
  const statusLabel = localizeSearchStatus(scope, status, t, i18n);

  const open = (): void => {
    if (onOpen !== undefined) {
      onOpen();
      return;
    }
    window.location.assign(url);
  };

  return (
    <PrimitiveButton
      type="button"
      className="search-result-card"
      data-variant={variant}
      data-scope={scope}
      aria-label={t('search.result.open', { title: titleLabel })}
      onClick={open}
    >
      <span className="search-result-card__icon" aria-hidden="true">
        {iconForScope(scope)}
      </span>
      <span className="search-result-card__body">
        <span className="search-result-card__eyebrow">{scopeLabel}</span>
        <strong className="search-result-card__title">
          <HighlightedText text={titleLabel} query={query} />
        </strong>
        {summaryLabel !== null ? (
          <span className="search-result-card__summary">
            <HighlightedText text={summaryLabel} query={query} />
          </span>
        ) : null}
      </span>
      <span className="search-result-card__meta">
        {showScope ? (
          <Badge tone="neutral" size="sm">
            {scopeLabel}
          </Badge>
        ) : null}
        {statusLabel !== null ? (
          <Badge tone="warning" size="sm">
            {statusLabel}
          </Badge>
        ) : null}
        {variant !== 'suggestion' ? <IcnArrowRight size={14} aria-hidden="true" /> : null}
      </span>
    </PrimitiveButton>
  );
}

interface HighlightedTextProps {
  text: string;
  query: string;
}

export function HighlightedText({ text, query }: HighlightedTextProps): ReactNode {
  const terms = termsForQuery(query);
  if (terms.length === 0) return text;

  const regex = new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'giu');
  const parts: ReactNode[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(regex)) {
    const matchText = match[0];
    const index = match.index;
    if (index > lastIndex) {
      parts.push(text.slice(lastIndex, index));
    }
    parts.push(
      <mark key={`${String(index)}:${matchText}`} className="search-highlight">
        {matchText}
      </mark>,
    );
    lastIndex = index + matchText.length;
  }

  if (parts.length === 0) return text;
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return <>{parts}</>;
}

function termsForQuery(query: string): string[] {
  const compact = query.trim().replace(/\s+/g, ' ');
  if (compact.length === 0) return [];
  const terms = [compact, ...compact.split(' ')];
  const seen = new Set<string>();

  return terms
    .filter((term) => term.length > 0)
    .filter((term) => {
      const key = term.toLocaleLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => b.length - a.length);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function localizeSearchTitle(
  scope: SearchHitScope,
  title: string,
  t: TFunction,
  i18n: I18nInstance,
): string {
  const key = `search.resultTitles.${scope}`;
  if ((scope === 'comments' || scope === 'annotations') && i18n.exists(key, { ns: 'common' })) {
    return t(key);
  }
  return title;
}

function localizeSearchSummary(
  scope: SearchHitScope,
  summary: string | null,
  metadata: Record<string, unknown> | undefined,
  t: TFunction,
  i18n: I18nInstance,
): string | null {
  if (scope === 'members') {
    const role = typeof metadata?.role === 'string' ? metadata.role : null;
    const roleLabel = role === null
      ? null
      : translateIfExists(`search.memberRoles.${role}`, t, i18n);
    if (roleLabel !== null) return roleLabel;
  }

  const trimmed = summary?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}

function localizeSearchStatus(
  scope: SearchHitScope,
  status: string | null,
  t: TFunction,
  i18n: I18nInstance,
): string | null {
  const trimmed = status?.trim() ?? '';
  if (trimmed.length === 0) return null;

  return (
    translateIfExists(`search.status.${scope}.${trimmed}`, t, i18n) ??
    translateIfExists(`search.status.common.${trimmed}`, t, i18n) ??
    titleCaseStatus(trimmed)
  );
}

function translateIfExists(
  key: string,
  t: TFunction,
  i18n: I18nInstance,
): string | null {
  return i18n.exists(key, { ns: 'common' }) ? t(key) : null;
}

function titleCaseStatus(status: string): string {
  return status
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\p{L}/u, (first) => first.toLocaleUpperCase());
}
